import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { InitialView } from './InitialView';
import { RecordingView } from './RecordingView';
import { Button } from '@/components/ui/Button';
import type { VoiceFileSelectHandler } from '../../../../types';
import { DEFAULT_VOICE_LANGUAGE, DEFAULT_VOICE_ACCENT } from '../../../../utils/constants';

interface VoiceCloneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFileSelect: VoiceFileSelectHandler;
  onRecordStart?: () => void;
}

export const VoiceCloneModal: React.FC<VoiceCloneModalProps> = ({
  isOpen,
  onClose,
  onFileSelect,
  onRecordStart,
}) => {
  const [view, setView] = React.useState<'initial' | 'recording'>('initial');
  const [fileSaved, setFileSaved] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setView('initial');
      setFileSaved(false);
    }
  }, [isOpen]);

  const handleStartRecording = () => {
    setView('recording');
    onRecordStart?.();
  };

  const handleFileSelect: VoiceFileSelectHandler = (file, language, accent) => {
    onFileSelect(file, language, accent);
    setFileSaved(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className='fixed inset-0 z-modal flex items-center justify-center p-4'>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='absolute inset-0 bg-black/40 backdrop-blur-sm'
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={clsx(
              'relative flex w-full max-w-[800px] flex-col overflow-hidden',
              'max-h-[90vh]',
              'rounded-20 border-4 border-brand-800',
              'bg-white shadow-2xl'
            )}
            style={{
              background: `linear-gradient(180deg, #EEEAFF -28.1%, #FFFFFF 63.55%)`,
            }}
          >
            <button
              onClick={onClose}
              className='absolute right-4 top-4 z-10 rounded-full bg-red-100 p-2 text-red-400 transition-colors hover:bg-red-200'
            >
              <XMarkIcon className='size-5' />
            </button>

            <div className='global-scrollbar flex flex-col gap-4 overflow-y-auto p-8 pt-14 md:p-10 md:pt-14'>
              {fileSaved ? (
                <div className='flex flex-col items-center gap-6 py-10'>
                  <CheckCircleIcon className='size-16 text-green-500' />
                  <p className='font-sans text-18 font-bold text-brand-800'>Voice file saved!</p>
                  <p className='font-sans text-14 font-normal text-brand-800/60'>
                    Voice will be cloned when you start training (10 credits)
                  </p>
                  <Button
                    onClick={onClose}
                    variant='settings-modify'
                    className='h-[48px] w-[154px] text-[16px] text-brand-800'
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <div className='flex flex-col items-center gap-2 text-center'>
                    <h2 className='mb-4 font-sans text-32 font-bold leading-27 text-brand-800'>
                      Clone Your Voice
                    </h2>
                    <p className='font-sans text-18 font-normal leading-24 text-brand-800'>
                      {view === 'recording'
                        ? 'Record at least 10 seconds of clear audio to create your voice clone'
                        : 'For best results, upload a clear 10-15 second audio clip (anything beyond 15s will be clipped).'}
                    </p>
                  </div>

                  <div className='h-px w-full border-t border-purple-170' />

                  <div className='mt-2'>
                    {view === 'initial' ? (
                      <InitialView
                        onFileSelect={(file) =>
                          handleFileSelect(file, DEFAULT_VOICE_LANGUAGE, DEFAULT_VOICE_ACCENT)
                        }
                        onRecordStart={handleStartRecording}
                      />
                    ) : (
                      <RecordingView onSave={handleFileSelect} />
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
